import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Chip,
  Divider,
  Button
} from "@mui/material";

import { CheckCircle, Language } from "@mui/icons-material";

import CustomCard from "../../components/common/CustomCard";
import CustomSnackbar from "../../components/common/CustomSnackbar";

const SUCCESS_REASON_MAP = {
  interview_completed: {
    title: "Interview Submitted Successfully",
    text: "Your interview has been submitted successfully. You may now close this tab.",
    chip: { label: "Interview Completed", color: "success", icon: <CheckCircle /> }
  },

  default: {
    title: "Success",
    text: "Your interview has been submitted successfully. You may now close this tab.",
    chip: { label: "Success", color: "success", icon: <CheckCircle /> }
  }
};

const SuccessPage = () => {
  const location = useLocation();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const reason = location.state?.reason || "default";
  const successInfo = SUCCESS_REASON_MAP[reason] || SUCCESS_REASON_MAP.default;

  // Show success snackbar
  useEffect(() => {
    const timer = setTimeout(() => {
      setSnackbar({
        open: true,
        message: successInfo.text,
        severity: "success"
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [successInfo]);

  // Block browser back navigation
  useEffect(() => {
    const blockBackNavigation = () => {
      window.history.pushState(null, "", window.location.href);
    };

    blockBackNavigation();

    const handlePopState = () => {
      blockBackNavigation();

      setSnackbar({
        open: true,
        message: "Interview completed. Please close this tab.",
        severity: "info"
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const goToCompany = useCallback(() => {
    window.open("https://www.cybermeru.com", "_blank");
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e9fbe7 0%, #c8f7c5 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >
      <Container maxWidth="lg">
          <CustomCard elevation={6} size={{ height: "100%", width: "100%" }}>
            <Box sx={{ textAlign: "center", p: 4 }}>
              {/* Success Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: `${successInfo.chip.color}.light`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px auto",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
                }}
              >
                {successInfo.chip.icon}
              </Box>

              {/* Title */}
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                {successInfo.title}
              </Typography>

              {/* Description */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {successInfo.text}
              </Typography>

              {/* Status Chip */}
              <Chip
                icon={successInfo.chip.icon}
                label={successInfo.chip.label}
                color={successInfo.chip.color}
                variant="outlined"
                sx={{ mb: 3 }}
              />

              <br />

              {/* Visit Company Button */}
              <Button
                variant="contained"
                startIcon={<Language />}
                onClick={goToCompany}
                sx={{
                  py: 1.3,
                  background: "linear-gradient(45deg, #2e7d32 30%, #66bb6a 90%)",
                  color: "white",
                  fontWeight: 600,
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #1b5e20 30%, #43a047 90%)"
                  },
                  mb: 3
                }}
              >
                Visit Company
              </Button>

              <Divider sx={{ my: 3 }} />

              <Typography variant="caption" display="block">
                Thank you for completing your interview with CyberMeru.
              </Typography>

              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                You may safely close this tab now.
              </Typography>
            </Box>
          </CustomCard>
      </Container>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false
          }))
        }
      />
    </Box>
  );
};

export default SuccessPage;

